import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getAttendanceDay,
  getAttendanceMonth,
  getAttendanceSession,
  saveAttendanceSession,
  deleteAttendanceSession,
} from '../../services/api';
import AttendanceReports from './AttendanceReports';
import { formatTime } from '../../utils/timeUtils';
import { getRumaalColor, getClassBackgroundColor } from '../../utils/colors';

// Late is blue, not amber — the old attendance app the team is coming from
// used a blue clock for Late, and the colors carry the muscle memory.
const STATUSES = [
  { value: 'present', label: 'P', word: 'Present', active: 'bg-green-600 text-white ring-green-600' },
  { value: 'absent', label: 'A', word: 'Absent', active: 'bg-red-500 text-white ring-red-500' },
  { value: 'late', label: 'L', word: 'Late', active: 'bg-sky-500 text-white ring-sky-500' },
];

// The studio's calendar day from the browser clock — the server never guesses
// a timezone.
const localToday = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const shiftDate = (date, days) => {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return next.toISOString().slice(0, 10);
};

const longDate = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

const classTitle = (cls) => cls.name || `${cls.type} — ${cls.ageGroup}`;

const draftKey = (classId, date) => `l2b-attendance-draft:${classId}:${date}`;

const SummaryChips = ({ summary, unmarked }) => (
  <span className="flex flex-wrap items-center gap-1.5 text-xs font-medium">
    <span className="rounded-full bg-green-50 px-2 py-0.5 text-green-700 ring-1 ring-green-200">
      {summary.present} P
    </span>
    <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-600 ring-1 ring-red-200">
      {summary.absent} A
    </span>
    <span className="rounded-full bg-sky-50 px-2 py-0.5 text-sky-600 ring-1 ring-sky-200">
      {summary.late} L
    </span>
    {unmarked > 0 && (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">
        {unmarked} unmarked
      </span>
    )}
  </span>
);

/**
 * Month grid with a dot on days that have marked sessions — the old app's
 * home screen, kept for the team's muscle memory. Tapping the date label in
 * the day bar toggles it; tapping a day jumps the day view there.
 */
const MonthCalendar = ({ date, setDate }) => {
  const [month, setMonth] = useState(date.slice(0, 7));
  const [days, setDays] = useState({});
  const today = localToday();

  useEffect(() => {
    let stale = false;
    getAttendanceMonth(month)
      .then((res) => {
        if (!stale) setDays(res.data.days);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [month]);

  const [y, m] = month.split('-').map(Number);
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString('en-CA', {
    month: 'long',
    year: 'numeric',
  });
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay(); // Sun-first grid
  const shiftMonth = (delta) => {
    const next = new Date(y, m - 1 + delta, 1);
    setMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
  };

  const cells = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => {
      const day = `${month}-${String(i + 1).padStart(2, '0')}`;
      return { num: i + 1, day, marked: (days[day] || 0) > 0 };
    }),
  ];

  return (
    <div className="mx-auto mb-4 max-w-sm rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-200">
      <div className="mb-2 flex items-center justify-between">
        <button
          onClick={() => shiftMonth(-1)}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
          aria-label="Previous month"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-gray-800">{monthLabel}</span>
        <button
          onClick={() => shiftMonth(1)}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
          aria-label="Next month"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs text-gray-400">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <span key={d} className="py-1">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 text-center">
        {cells.map((cell, i) =>
          cell === null ? (
            <span key={`pad-${i}`} />
          ) : (
            <button
              key={cell.day}
              onClick={() => setDate(cell.day)}
              className={`mx-auto flex h-9 w-9 flex-col items-center justify-center rounded-full text-sm ${
                cell.day === date
                  ? 'bg-emerald-600 font-semibold text-white'
                  : cell.day === today
                    ? 'bg-emerald-50 font-semibold text-emerald-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {cell.num}
              <span
                className={`h-1 w-1 rounded-full ${
                  cell.marked
                    ? cell.day === date
                      ? 'bg-white'
                      : 'bg-emerald-500'
                    : 'bg-transparent'
                }`}
              />
            </button>
          )
        )}
      </div>
    </div>
  );
};

const ClassCard = ({ cls, note, onOpen }) => (
  <button
    onClick={() => onOpen(cls)}
    className="w-full rounded-lg border-l-4 bg-white p-3 text-left shadow-sm ring-1 ring-gray-200 transition-shadow hover:shadow-md"
    style={{ borderLeftColor: getClassBackgroundColor(cls.type, cls.ageGroup) }}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-gray-900">{formatTime(cls.startTime)}</span>
          <span className="truncate text-sm font-medium text-gray-700">{classTitle(cls)}</span>
        </div>
        <div className="mt-0.5 truncate text-xs text-gray-500">
          {[cls.instructor, cls.location, `${cls.studentCount} students`]
            .filter(Boolean)
            .join(' · ')}
          {note && <span className="ml-1.5 font-medium text-amber-600">{note}</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {cls.session ? (
          <SummaryChips summary={cls.session.summary} unmarked={0} />
        ) : (
          <span className="text-xs text-gray-400">Not marked yet</span>
        )}
        <svg className="h-4 w-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  </button>
);

/**
 * Day view: the classes scheduled on the chosen date's weekday, each with its
 * marking state. Off-schedule classes that were marked anyway (makeups) are
 * shown too; the rest hide behind a toggle so the daily routine stays short.
 */
const DayView = ({ date, setDate, onOpen, refreshToken }) => {
  const [day, setDay] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showOthers, setShowOthers] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const today = localToday();

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setError('');
    getAttendanceDay(date)
      .then((res) => {
        if (!stale) setDay(res.data);
      })
      .catch(() => {
        if (!stale) setError('Could not load attendance — try refreshing the page');
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [date, refreshToken]);

  const scheduled = useMemo(
    () => (day?.classes || []).filter((c) => c.scheduledToday),
    [day]
  );
  const makeups = useMemo(
    () => (day?.classes || []).filter((c) => !c.scheduledToday && c.session),
    [day]
  );
  const others = useMemo(
    () => (day?.classes || []).filter((c) => !c.scheduledToday && !c.session),
    [day]
  );

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-4 flex items-center justify-center gap-2">
        <button
          onClick={() => setDate(shiftDate(date, -1))}
          className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
          aria-label="Previous day"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setShowCalendar((v) => !v)}
          className="flex items-center gap-1.5 rounded-md px-3 py-2 text-center font-semibold text-gray-900 hover:bg-gray-100"
          aria-label="Toggle month calendar"
        >
          {longDate(date)}
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform ${showCalendar ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <button
          onClick={() => setDate(shiftDate(date, 1))}
          className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
          aria-label="Next day"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        {date !== today && (
          <button
            onClick={() => setDate(today)}
            className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-200"
          >
            Today
          </button>
        )}
        {scheduled.length > 0 && (
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              scheduled.filter((c) => c.session).length === scheduled.length
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            Done {scheduled.filter((c) => c.session).length}/{scheduled.length}
          </span>
        )}
      </div>

      {showCalendar && <MonthCalendar date={date} setDate={setDate} />}

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
      ) : (
        day && (
          <>
            {scheduled.length === 0 ? (
              <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200">
                No classes are scheduled on {day.weekday}s.
              </div>
            ) : (
              <div className="space-y-2">
                {scheduled.map((cls) => (
                  <ClassCard key={cls.id} cls={cls} onOpen={onOpen} />
                ))}
              </div>
            )}

            {makeups.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Also marked this day
                </h3>
                <div className="space-y-2">
                  {makeups.map((cls) => (
                    <ClassCard
                      key={cls.id}
                      cls={cls}
                      note={`usually ${cls.day}s`}
                      onOpen={onOpen}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowOthers((v) => !v)}
                className="text-xs font-medium text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
              >
                {showOthers ? 'Hide other classes' : 'Mark a different class (makeup)'}
              </button>
              {showOthers && (
                <div className="mt-3 space-y-2 text-left">
                  {others.map((cls) => (
                    <ClassCard
                      key={cls.id}
                      cls={cls}
                      note={`usually ${cls.day}s`}
                      onOpen={onOpen}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )
      )}
    </div>
  );
};

const StatusButtons = ({ current, onPick }) => (
  <div className="flex shrink-0 gap-1.5">
    {STATUSES.map((s) => (
      <button
        key={s.value}
        onClick={() => onPick(s.value)}
        title={s.word}
        aria-label={s.word}
        aria-pressed={current === s.value}
        className={`h-9 w-11 rounded-md text-xs font-bold ring-1 transition-colors ${
          current === s.value
            ? s.active
            : 'bg-white text-gray-400 ring-gray-200 hover:bg-gray-50'
        }`}
      >
        {s.label}
      </button>
    ))}
  </div>
);

/**
 * Marking screen. Every tap autosaves the whole session (the server upsert is
 * idempotent) and is also mirrored to localStorage, so studio wifi dropping —
 * or the tab dying — never loses marks. Tapping a student's active status
 * again un-marks them: missing means "unmarked", never "absent".
 */
const MarkingScreen = ({ cls, date, onBack }) => {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [statusByKey, setStatusByKey] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved | error
  const [savedInfo, setSavedInfo] = useState(null); // { markedBy, updatedAt }

  const statusRef = useRef(statusByKey);
  const hadSession = useRef(false);
  const saveTimer = useRef(null);
  const retryTimer = useRef(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    let stale = false;
    getAttendanceSession(cls.id, date)
      .then((res) => {
        if (stale) return;
        const data = res.data;
        hadSession.current = !!data.session;
        if (data.session) {
          setSavedInfo({ markedBy: data.session.markedBy, updatedAt: data.session.updatedAt });
        }
        const fromServer = {};
        for (const entry of data.session?.entries || []) {
          fromServer[entry.studentKey] = entry.status;
        }
        // A draft outlives a crashed tab or dropped connection; it is newer
        // than whatever the server has, so it wins and gets re-saved.
        let merged = fromServer;
        try {
          const draft = JSON.parse(localStorage.getItem(draftKey(cls.id, date)) || 'null');
          if (draft?.statusByKey) {
            merged = draft.statusByKey;
          }
        } catch {
          // Unreadable draft — server state stands.
        }
        statusRef.current = merged;
        setStatusByKey(merged);
        setPayload(data);
        if (JSON.stringify(merged) !== JSON.stringify(fromServer)) {
          markDirty();
        }
      })
      .catch(() => {
        if (!stale) setError('Could not load this class — go back and try again');
      });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls.id, date]);

  const doSave = useCallback(async () => {
    clearTimeout(saveTimer.current);
    clearTimeout(retryTimer.current);
    dirtyRef.current = false;

    const entries = Object.entries(statusRef.current).map(([studentKey, status]) => ({
      studentKey,
      status,
    }));

    try {
      if (entries.length === 0) {
        if (hadSession.current) {
          setSaveState('saving');
          await deleteAttendanceSession(cls.id, date);
          hadSession.current = false;
          setSavedInfo(null);
        }
        localStorage.removeItem(draftKey(cls.id, date));
        setSaveState(dirtyRef.current ? 'dirty' : 'idle');
        return;
      }
      setSaveState('saving');
      const res = await saveAttendanceSession(cls.id, date, entries);
      hadSession.current = true;
      localStorage.removeItem(draftKey(cls.id, date));
      setSavedInfo({
        markedBy: res.data.session.markedBy,
        updatedAt: res.data.session.updatedAt,
      });
      // Taps that landed while the request was in flight still need saving.
      if (dirtyRef.current) {
        setSaveState('dirty');
        saveTimer.current = setTimeout(doSave, 800);
      } else {
        setSaveState('saved');
      }
    } catch {
      // The draft is still in localStorage; keep retrying quietly.
      setSaveState('error');
      retryTimer.current = setTimeout(doSave, 4000);
    }
  }, [cls.id, date]);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    setSaveState('dirty');
    try {
      localStorage.setItem(
        draftKey(cls.id, date),
        JSON.stringify({ statusByKey: statusRef.current, at: Date.now() })
      );
    } catch {
      // Storage full/blocked — autosave alone still covers us.
    }
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(doSave, 1000);
  }, [cls.id, date, doSave]);

  // Flush a pending save when leaving the screen; the draft catches the rest.
  useEffect(
    () => () => {
      clearTimeout(saveTimer.current);
      clearTimeout(retryTimer.current);
      if (dirtyRef.current) doSave();
    },
    [doSave]
  );

  const setStatus = (studentKey, status) => {
    const next = { ...statusRef.current };
    if (next[studentKey] === status) {
      delete next[studentKey]; // tap again to un-mark
    } else {
      next[studentKey] = status;
    }
    statusRef.current = next;
    setStatusByKey(next);
    markDirty();
  };

  const allPresent = () => {
    // Fills only unmarked rows — absents and lates already tapped are kept.
    // The button's label owns the honesty: it reads "All present" only while
    // nothing is marked, and "Rest present" once anything is.
    const next = { ...statusRef.current };
    for (const student of payload?.roster || []) {
      if (!next[student.studentKey]) next[student.studentKey] = 'present';
    }
    statusRef.current = next;
    setStatusByKey(next);
    markDirty();
  };

  const summary = useMemo(() => {
    const counts = { present: 0, absent: 0, late: 0 };
    for (const status of Object.values(statusByKey)) counts[status] += 1;
    return counts;
  }, [statusByKey]);

  const unmarked = (payload?.roster || []).filter((s) => !statusByKey[s.studentKey]).length;

  if (error) {
    return (
      <div className="mx-auto max-w-2xl">
        <button onClick={onBack} className="mb-3 text-sm text-gray-500 hover:text-gray-700">
          ← Back
        </button>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!payload) {
    return <div className="py-10 text-center text-sm text-gray-400">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-2xl pb-24">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <button onClick={onBack} className="mb-1 text-sm text-gray-500 hover:text-gray-700">
            ← All classes
          </button>
          <h2 className="text-lg font-bold text-gray-900">{classTitle(cls)}</h2>
          <p className="text-sm text-gray-500">
            {longDate(date)} · {formatTime(cls.startTime)}
            {cls.instructor && ` · ${cls.instructor}`}
          </p>
        </div>
        <button
          onClick={allPresent}
          disabled={unmarked === 0}
          title="Marks every student without a status as present"
          className="shrink-0 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-green-600"
        >
          ✓ {unmarked === payload.roster.length ? 'All present' : 'Rest present'}
        </button>
      </div>

      <p className="mb-2 text-xs text-gray-400">
        P = Present · A = Absent · L = Late — tap again to un-mark
      </p>

      <div className="divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {payload.roster.map((student) => (
          <div
            key={student.studentKey}
            className="flex items-center justify-between gap-3 px-3 py-2"
          >
            <div className="flex min-w-0 items-center gap-2">
              {student.rumaalRank && (
                // A folded rumal (kerchief) in the rank color — the one shape
                // that means "rank" here and can't be mistaken for a control.
                <span className="shrink-0" title={`${student.rumaalRank} rumaal`}>
                  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M4 6h16l-6.6 11.6a1.8 1.8 0 01-2.8 0L4 6z"
                      fill={getRumaalColor(student.rumaalRank)}
                      stroke="#9CA3AF"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
              <span className="truncate text-sm font-medium text-gray-800">{student.name}</span>
              {student.status !== 'Active' && (
                <span className="shrink-0 text-xs text-gray-400">{student.status}</span>
              )}
            </div>
            <StatusButtons
              current={statusByKey[student.studentKey]}
              onPick={(status) => setStatus(student.studentKey, status)}
            />
          </div>
        ))}
        {payload.roster.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">
            This class has no students yet.
          </div>
        )}
      </div>

      {payload.departed.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            No longer in this class
          </h3>
          <div className="divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
            {payload.departed.map((student) => (
              <div
                key={student.studentKey}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span className="truncate text-sm text-gray-500">{student.name}</span>
                <StatusButtons
                  current={statusByKey[student.studentKey]}
                  onPick={(status) => setStatus(student.studentKey, status)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="fixed inset-x-0 bottom-0 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
          <SummaryChips summary={summary} unmarked={unmarked} />
          <span className="shrink-0 text-xs">
            {saveState === 'saving' && <span className="text-gray-400">● Saving…</span>}
            {saveState === 'dirty' && <span className="text-gray-400">● Unsaved</span>}
            {saveState === 'saved' && (
              <span className="text-green-600">
                ✓ Saved{savedInfo?.markedBy ? ` · ${savedInfo.markedBy}` : ''}
              </span>
            )}
            {saveState === 'error' && (
              <button onClick={doSave} className="font-medium text-red-600 hover:underline">
                ⚠ Couldn't save — tap to retry
              </button>
            )}
            {saveState === 'idle' && savedInfo?.markedBy && (
              <span className="text-gray-400">Marked by {savedInfo.markedBy}</span>
            )}
            {saveState === 'idle' && !savedInfo && (
              <span className="text-gray-400">Not marked yet</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

const Attendance = () => {
  const [mode, setMode] = useState('mark'); // mark | reports
  const [date, setDate] = useState(localToday());
  const [openClass, setOpenClass] = useState(null);
  // Bumped whenever the marking screen closes, so the day view's summaries
  // refetch and reflect the marks that were just made.
  const [refreshToken, setRefreshToken] = useState(0);

  if (openClass) {
    return (
      <MarkingScreen
        cls={openClass}
        date={date}
        onBack={() => {
          setOpenClass(null);
          setRefreshToken((n) => n + 1);
        }}
      />
    );
  }

  return (
    <div>
      <div className="mb-5 flex justify-center">
        <div className="inline-flex rounded-lg bg-gray-100 p-1">
          {[
            { key: 'mark', label: 'Mark' },
            { key: 'reports', label: 'Reports' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setMode(tab.key)}
              className={`rounded-md px-5 py-1.5 text-sm font-medium transition-colors ${
                mode === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {mode === 'mark' ? (
        <DayView date={date} setDate={setDate} onOpen={setOpenClass} refreshToken={refreshToken} />
      ) : (
        <AttendanceReports />
      )}
    </div>
  );
};

export default Attendance;
